import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tsTemplatesApi } from '@/api/services/tsTemplates';
import type { CreateUpdateTsAlertTemplateInput } from '@/api/types/tsTemplates';

export const tsTemplateKeys = {
  all: ['tsTemplates'] as const,
  active: ['tsTemplates', 'active'] as const,
  deleted: ['tsTemplates', 'deleted'] as const,
  detail: (id: number) => ['tsTemplates', 'detail', id] as const,
};

export function useTemplateDetail(id: number | null) {
  return useQuery({
    queryKey: tsTemplateKeys.detail(id ?? -1),
    queryFn: () => tsTemplatesApi.get(id as number),
    enabled: id !== null,
  });
}

export function useSetTemplateEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: number; enabled: boolean }) =>
      tsTemplatesApi.setEnabled(id, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: tsTemplateKeys.active }),
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => tsTemplatesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: tsTemplateKeys.all }),
  });
}

export function useCreateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUpdateTsAlertTemplateInput) => tsTemplatesApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tsTemplateKeys.all }),
  });
}

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateUpdateTsAlertTemplateInput }) => tsTemplatesApi.update(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tsTemplateKeys.all }),
  });
}

export function usePriorities() {
  return useQuery({ queryKey: ['tsTemplates', 'priorities'], queryFn: () => tsTemplatesApi.getPriorities(), staleTime: Infinity });
}
